import { queryClient } from './queryClient';
import { getHostingEnvironmentDomain } from './utils';

export interface BlueprintVersionsDocument {
  schemaVersion?: string;
  environment?: string;
  assetBaseUrl: string;
  applicationSlug: string;
  registry?: {
    entry?: string;
  };
  versions: readonly { version: string; status?: string }[];
}

const DEFAULT_REGISTRY_ENTRY = 'index.js';

/**
 * Blueprint CMS API origin for the current PSP hosting environment.
 */
export function getBlueprintApiBaseUrl(): string {
  return `https://api.blueprint.${getHostingEnvironmentDomain()}`;
}

/**
 * blueprint-mfe-registry `/versions.json` URL for the current PSP hosting environment.
 */
export function getBlueprintRegistryUrl(): string {
  return `https://mfe-registry.blueprint.${getHostingEnvironmentDomain()}/versions.json`;
}

/**
 * Compare two semver strings for descending sort (highest version first).
 * Blueprint registry versions are simple `major.minor.patch` strings.
 */
export function compareBlueprintSemverDesc(a: string, b: string): number {
  const parse = (version: string): number[] =>
    version.split('.').map((part) => {
      const numeric = Number.parseInt(part, 10);
      return Number.isNaN(numeric) ? 0 : numeric;
    });

  const aParts = parse(a);
  const bParts = parse(b);
  const length = Math.max(aParts.length, bParts.length);

  for (let i = 0; i < length; i++) {
    const diff = (bParts[i] ?? 0) - (aParts[i] ?? 0);
    if (diff !== 0) return diff;
  }

  return 0;
}

/**
 * Picks the highest non-deprecated semver from the registry document,
 * regardless of array ordering in versions.json.
 */
export function pickLatestStableBlueprintVersion(
  versions: readonly { version: string; status?: string }[]
): string | undefined {
  if (versions.length === 0) return undefined;

  const stableVersions = versions.filter((entry) => entry.status !== 'deprecated');
  const candidates = stableVersions.length > 0 ? [...stableVersions] : [...versions];
  candidates.sort((a, b) => compareBlueprintSemverDesc(a.version, b.version));
  return candidates[0]?.version;
}

/**
 * Builds the immutable CMS bundle URL from a registry document and target version.
 */
export function buildBlueprintModuleUrl(
  document: BlueprintVersionsDocument,
  version: string
): string {
  const assetBaseUrl = document.assetBaseUrl.replace(/\/$/, '');
  const entry = document.registry?.entry ?? DEFAULT_REGISTRY_ENTRY;
  return `${assetBaseUrl}/${document.applicationSlug}/${version}/${entry}`;
}

async function fetchBlueprintVersionsDocument(
  registryUrl: string
): Promise<BlueprintVersionsDocument> {
  return queryClient.fetchQuery({
    queryKey: ['blueprintVersions', registryUrl],
    queryFn: async () => {
      const response = await fetch(registryUrl, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(
          `[Blueprint] Failed to fetch ${registryUrl}: ${response.status} ${response.statusText}`
        );
      }
      return (await response.json()) as BlueprintVersionsDocument;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Resolves the Blueprint CMS `index.js` URL by fetching blueprint-mfe-registry
 * `/versions.json` and constructing `{assetBaseUrl}/{applicationSlug}/{version}/{entry}`.
 */
export async function resolveBlueprintModuleUrl(): Promise<string> {
  const registryUrl = getBlueprintRegistryUrl();

  try {
    const document = await fetchBlueprintVersionsDocument(registryUrl);
    const version = pickLatestStableBlueprintVersion(document.versions);
    if (!version) {
      console.error('[Blueprint] versions.json contained no usable version entries');
      return '';
    }
    return buildBlueprintModuleUrl(document, version);
  } catch (err) {
    console.error('[Blueprint] Failed to resolve CMS module URL:', err);
    return '';
  }
}
